import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager";
import bodyStylesManager from "../../../stores/body-styles-manager";
import { IHtmlBlockData } from "../../../types/content-block-data";

import SatoriTextBox from "../shared/satori-ui/textbox";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import { cleanDimenValues } from "../../../lib/page-builder-lib";
import SatoriMultiLineBox from "../shared/satori-ui/multilinebox";

/**
 * CustomHtmlOptions – Sidebar Editing Panel for Custom HTML Blocks
 *
 * Provides a sidebar panel for editing the raw HTML or JavaScript code and padding
 * for a Custom HTML content block in the Satori PageBuilder. Allows direct input of
 * custom HTML, embeds, and widgets, with responsive spacing controls (px/rem/em).
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Custom HTML content block to edit.
 *
 * @example
 * // Render custom HTML options panel for a given Custom HTML block
 * <CustomHtmlOptions blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Custom HTML block's data from the MobX contentManager store.
 * - Provides a multiline editor for raw HTML or JavaScript (suitable for embeds, widgets, scripts, etc).
 * - Padding control supports px/rem/em units with automatic conversion based on parent font size.
 * - All field changes call `contentManager.setHtmlConfig` for atomic and reactive MobX state updates.
 * - Fully reactive via MobX's `observer()` wrapper.
 * - Mirrors the options panel structure of VideoOptions and TextStyles for consistent UX.
 *
 * @see contentManager.setHtmlConfig
 * @see VideoOptions, TextStyles – For parallel sidebar editing panels
 */
export default observer(function CustomHtmlOptions({blockId}: {blockId: string}) {
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "html") return null;
  const htmlData = block.blockData as IHtmlBlockData;

	const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
  let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)

  if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
    parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
  else
    parentRawFontSize = parseInt(parentFontSize!.value);

  function update(field: string, value: any) {
    contentManager.setHtmlConfig(blockId, {
      ...htmlData,
      [field]: value,
    });
  }

  return (
    <section className="flex flex-col p-4 gap-3">
      <SatoriMultiLineBox
        label={"Custom Html/JavaScript"}
        value={htmlData.html || "<strong>Hello World!</strong>"}
        onChange={val => update("html", val)}
      />
      <SatoriDimensionsBox
        label={"Padding"}
        value={cleanDimenValues(htmlData.padding.value)}
        unit={htmlData.padding.unit}
        onValueChange={val => 
          update("padding", {
            value: val,
            unit: htmlData.padding.unit,
          })
        }
        onUnitChange={unitVal => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            const convertedValue = Math.round(
              parseFloat(htmlData?.padding?.value) * parentRawFontSize
            ).toString();
            update("padding", {
              value: convertedValue,
              unit: unitVal,
            })
          } else if (unitVal === "rem" || unitVal === "em") {
            const convertedValue = (
              parseFloat(htmlData?.padding?.value) / parentRawFontSize
            ).toFixed(3).toString();
            update("padding", {
              value: convertedValue,
              unit: unitVal,
            })
          }
        }}
      />
    </section>
  );
});
